import { Report } from '@/app/models/costTracking';
import { useUpdateCostTrackingMutation } from '@/app/services/costTrackingApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ReportItem from '../ReportItem';

interface NewEditFinalBalanceProps {
  selectItem: Report[];
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditFinalBalance(props: NewEditFinalBalanceProps) {
  const { t } = useTranslation(['costTracking', 'common']);
  const theme = useTheme();
  const { selectItem, setDrawerOpen } = props;

  const [reports, setReports] = useState(() => {
    const reportSections = [{ name: 'fulfilled', type: 'REPLANNED' }];

    return reportSections.map((section) => {
      const report = selectItem.find((r) => r.planningReportType === section.type);
      return {
        id: report?.id,
        planningReportType: section.name,
        value: report?.value || 0,
        justify: report?.justify || '',
      };
    });
  });

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleJustificationChange = (index: number, newValue: string) => {
    const updatedReports = [...reports];
    updatedReports[index].justify = newValue;
    setReports(updatedReports);
  };

  const [update] = useUpdateCostTrackingMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        list: reports.map((report) => ({
          id: report.id as number,
          justify: report.justify,
        })),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:comments.saved.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <ModalPageTitle title={t('comment.rubric.values')} />
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mb: 2 }} />

        {reports.map((item, index) => (
          <ReportItem
            key={item.id}
            name={t(item.planningReportType)}
            value={item.value}
            justify={item.justify as string}
            index={index}
            isLast={index === reports.length - 1}
            onJustifyChangeWithIndex={handleJustificationChange}
          />
        ))}
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} />
      </Box>
    </Box>
  );
}
